import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Chip,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UpgradeDialogSource } from "schemas/dashboard";
import { routes } from "schemas/routes";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import {
  OrganizationTemplateRubric,
  Template,
  TemplateContentType,
  TemplateRubricName,
} from "features/Influencer/ContactList/schema";
import { getLatestRubricByName } from "features/Influencer/Templates/TemplatesTable/helpers";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";

import HalfDoughnut from "./HalfDoughnut";
import Rubrics from "./Rubrics";

interface Props {
  template: Template;
  setTemplate: React.Dispatch<React.SetStateAction<Template>>;
}

const TemplateRubrics = ({ template, setTemplate }: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setTemplates, setCreateTemplateDialogOpen } = useContext(
    OutreachTemplatesContext,
  );
  const {
    setUpgradeDialogSource,
    remainingTemplateEvaluations,
    totalTemplateEvaluations,
    setRemainingTemplateEvaluations,
  } = useContext(SubscriptionContext);
  const [rubric, setRubric] = useState<OrganizationTemplateRubric | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleEvaluate = async () => {
    if (
      remainingTemplateEvaluations !== null &&
      remainingTemplateEvaluations <= 0
    ) {
      setUpgradeDialogSource(UpgradeDialogSource.template_evaluation_limit);
      return;
    }
    try {
      setLoading(true);
      setRemainingTemplateEvaluations((prev) =>
        prev !== null ? prev - 1 : prev,
      );
      const createNewTemplate = !template?.id;
      const { rubric, template: updatedTemplate } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/evaluate`,
        "POST",
        {},
        {
          // We still need to pass subject and body in as user might change
          // the template and re-evaluate without saving the template
          templateId: template?.id,
          body: template?.body,
          subject: template?.subject,
          contentType: template?.contentType,
          name: template?.name,
          attachment: template?.attachment,
        },
      );
      setRubric(rubric);
      if (updatedTemplate) {
        setTemplate(updatedTemplate);
        setTemplates((prev) =>
          prev.map((t) => (t.id === updatedTemplate.id ? updatedTemplate : t)),
        );
      }
      // If the current URL does not have a template id, navigate to that
      if (isPage(routes.templates) && createNewTemplate) {
        setCreateTemplateDialogOpen(false);
        navigate(`/${routes.templates}/${updatedTemplate.id}`, {
          replace: true,
        });
      }
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes("hit the limit of using the template evaluator")
      ) {
        setUpgradeDialogSource(UpgradeDialogSource.template_evaluation_limit);
      } else {
        setErrorAlert(error);
      }
      setRemainingTemplateEvaluations((prev) =>
        prev !== null ? prev + 1 : prev,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const rubricName =
      template.contentType === TemplateContentType.FOLLOWUP
        ? TemplateRubricName.followup_default
        : TemplateRubricName.outreach_default;
    const latestRubric = getLatestRubricByName(rubricName, template);
    setRubric(latestRubric);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.rubrics?.length, template?.contentType]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Chip
          size="small"
          label={
            <span>
              {`${(totalTemplateEvaluations || 0) - (remainingTemplateEvaluations || 0)}/${totalTemplateEvaluations ?? "—"} evaluations used`}
            </span>
          }
          sx={{
            backgroundColor: "white",
            border: "1px solid",
            borderColor: "black",
            color: grey[800],
            px: 1,
            cursor: !currentOrg?.isPaidUser ? "pointer" : "default",
            fontSize: 12,
          }}
          onClick={() =>
            !currentOrg?.isPaidUser
              ? setUpgradeDialogSource(
                  UpgradeDialogSource.template_limit_increase_chip,
                )
              : null
          }
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={
            loading
              ? {
                  background:
                    "linear-gradient(90deg, #555 0%, #000 40%, #555 60%, #000 100%)",
                  backgroundSize: "200% auto",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  animation: "shimmer 1.8s linear infinite",
                  "@keyframes shimmer": {
                    "0%": { backgroundPosition: "200% center" },
                    "100%": { backgroundPosition: "0% center" },
                  },
                }
              : {}
          }
        >
          {loading
            ? "We are evaluating your template... "
            : rubric
              ? "Here's Your Latest Template Score 🚀"
              : "Evaluate my template 👀"}
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: rubric ? 1 : 3 }}>
        {loading ? (
          <>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                animation: "bobUpDown 1.2s ease-in-out infinite",
                "@keyframes bobUpDown": {
                  "0%, 100%": { transform: "translateY(0)" },
                  "50%": { transform: "translateY(-4px)" },
                },
              }}
            >
              ⏳
            </Box>
            {" This may take up to a minute..."}
          </>
        ) : rubric ? (
          "Your evaluation is complete—review the full breakdown below."
        ) : (
          <>
            {
              "Get an instant breakdown of your template's strengths and areas to improve."
            }
          </>
        )}
      </Typography>

      {rubric && !loading && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 3, display: "block" }}
        >
          Created on:{" "}
          {new Date(rubric.createdAt * 1000).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </Typography>
      )}

      <HalfDoughnut rubric={rubric} loading={loading} />

      {!loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <StyledLargeButton
            onClick={handleEvaluate}
            disabled={
              !template || !(template?.body && template?.body?.length > 0)
            }
          >
            {rubric && rubric?.fields.length > 0
              ? "Re-evaluate My Template"
              : "Evaluate My Template"}
          </StyledLargeButton>
        </Box>
      )}

      {rubric && !loading && (
        <Rubrics rubric={rubric} showAll={isMobileScreen} />
      )}
    </Paper>
  );
};

export default TemplateRubrics;
